/**
 * The `FontMetrics` for a runtime-loaded scene font, as pure arithmetic. The DOM calls arrive as
 * the injected `measureWidthUnits`, so the module runs under happy-dom with a fake measurer.
 */
/** Mixed-case Latin plus digits, standing in for the OS/2 `xAvgCharWidth` that `sfntTables.ts` does not read. */
const AVERAGE_ADVANCE_SAMPLE = 'ABCXYZabcxyz0123456789';
/** Narrows a `FontMetrics` to `CanvasFontMetrics`, which names the CSS family a painter sets `ctx.font` to. */
export function isCanvasFontMetrics(metrics) {
    return metrics?.kind === 'canvas';
}
/**
 * Builds a `CanvasFontMetrics` from raw scalars and an injected width measurer. It memoises each
 * advance and pair, because the measurer is a DOM call and the shaper asks once per character and
 * pair on every shape.
 */
export function createRuntimeFontMetrics(input) {
    const { scalars, measureWidthUnits, cssFontFamily } = input;
    const advanceCache = new Map();
    const kerningCache = new Map();
    let averageAdvanceUnitsCache;
    function measureChar(ch) {
        let width = advanceCache.get(ch);
        if (width === undefined) {
            width = measureWidthUnits(ch);
            advanceCache.set(ch, width);
        }
        return width;
    }
    return {
        kind: 'canvas',
        cssFontFamily,
        unitsPerEm: scalars.unitsPerEm,
        ascent: scalars.ascent,
        descent: scalars.descent,
        // Never `null`: canvas has no "no glyph" signal, and the measured width is what `fillText`
        // paints. It carries the real `hmtx` advance, not a hand-parsed one, though a measured float
        // can differ by one 1/64 step from the integer `hmtx` value after quantisation.
        getGlyphAdvanceUnits(ch) {
            return measureChar(ch);
        },
        // From canvas, not a hand-parsed `kern` table: a measured pair carries the OpenType `GPOS`
        // kerning, which a `kern` reader misses.
        getKerningAdjustmentUnits(a, b) {
            const key = `${a}\0${b}`;
            let adjustment = kerningCache.get(key);
            if (adjustment === undefined) {
                adjustment = measureWidthUnits(a + b) - measureChar(a) - measureChar(b);
                kerningCache.set(key, adjustment);
            }
            return adjustment;
        },
        get averageAdvanceUnits() {
            if (averageAdvanceUnitsCache === undefined) {
                averageAdvanceUnitsCache = measureWidthUnits(AVERAGE_ADVANCE_SAMPLE) / AVERAGE_ADVANCE_SAMPLE.length;
            }
            return averageAdvanceUnitsCache;
        },
    };
}
//# sourceMappingURL=runtimeFontMetrics.js.map